import {ElementCursor, ElementProperties, ElementView} from '@app/modules/svg/element/ElementView';
import {MeshView, StyledEdge} from '@app/modules/svg/element/shape/pointed/MeshView';
import {Container} from '@app/modules/svg/Container';
import {Rect} from '@app/modules/svg/model/Rect';
import {MoveDrawable} from '@app/modules/svg/service/tool/draw/type/MoveDrawable';
import {ElementType} from '@app/modules/svg/dataSource/constant/ElementType';
import {LineTo} from '@app/modules/svg/model/path/line/LineTo';
import {Style} from '@app/modules/svg/service/style/Style';
import {PathCommand} from '@app/modules/svg/model/path/PathCommand';
import {Point} from '@app/modules/svg/model/Point';
import {Polygon} from '@app/modules/svg/service/math/Polygon';
import {Line} from '@app/modules/svg/model/Line';
import {Triangle} from '@app/modules/svg/model/Triangle';
import {linesEqual} from '@app/modules/svg/service/math/Line';
import {isPointInTriangle} from "@app/modules/svg/service/math/Traingle";

export class PyramidCursor extends ElementCursor {}

export interface PyramidProperties extends ElementProperties {
  baseVertexCount?: number;
}

export class PyramidView extends MeshView implements MoveDrawable {
  protected _type: ElementType = ElementType.PYRAMID;

  public constructor(container: Container,
                     properties: PyramidProperties = {},
                     rect: Rect = {x: 0, y: 0, width: 1, height: 1},
                     ownerId?: string, index?: number) {

    const cube = PyramidView.rectToPyramidMesh(rect, properties.baseVertexCount);
    super(container, properties, cube.points, cube.edges, ownerId, index);
    this.svgElement.id = this.id;

    this._rect = ElementView.calculateRect(this.points);
    this.__fixRect__();

    this.setProperties(properties);
  }

  public __drawSize__(rect: Rect): void {
    const cube = PyramidView.rectToPyramidMesh(rect, (this.properties as PyramidProperties).baseVertexCount);
    this._points = cube.points;
    this._styledEdges = cube.edges;

    this._rect = ElementView.calculateRect(this.points);

    this.__updateView__();
  }

  private static rectToPyramidMesh(rect: Rect, baseVertexCount: number): { points: Point[]; edges: StyledEdge[] } {
    const dashLineStyle: Style = new Style();
    dashLineStyle.strokeDashArray = '4 4';

    const baseCenter: Point = {
      x: rect.x + rect.width / 2,
      y: rect.y + rect.height * (5/6),
    };
    const baseAsPolygon: Point[] = Polygon.getEllipticPoints({
      cx: baseCenter.x,
      cy: baseCenter.y,
      rx: rect.width / 2,
      ry: rect.height * (1/6)
    }, baseVertexCount);
    const topVertex: Point = {
      x: rect.x + rect.width / 2,
      y: rect.y
    };

    const points: Point[] = [
      topVertex
    ];

    for (const point of baseAsPolygon) {
      points.push(point);
    }

    const firstPoint: Point = baseAsPolygon[0];
    const sweepLine: Line = baseAsPolygon
        .reduce((line: Line, point: Point): Line => {
          if (point.x <= line.p0.x) {
            line.p0 = point;
          }

          if (point.x >= line.p1.x) {
            line.p1 = point;
          }

          return line;
        }, { p0: firstPoint, p1: firstPoint });

    const boundingTriangle: Triangle = {
      p0: topVertex,
      p1: sweepLine.p0,
      p2: sweepLine.p1,
    };

    const edges: StyledEdge[] = [];
    for (let pointIndex = 0; pointIndex < baseAsPolygon.length; pointIndex++) {
      const nextPointIndex: number = pointIndex + 1 < baseAsPolygon.length ? pointIndex + 1 : 0;

      const currentPoint: Point = baseAsPolygon[pointIndex];
      const nextPoint: Point = baseAsPolygon[nextPointIndex];

      const isCurrentPointVisible: boolean = isPointInTriangle(currentPoint, boundingTriangle);
      const isNextPointVisible: boolean = isPointInTriangle(nextPoint, boundingTriangle);

      const edgeFromTopVertex: StyledEdge = {point1: 0, point2: pointIndex + 1, command: new LineTo({x: 0, y: 0})};
      if (isCurrentPointVisible) {
        edgeFromTopVertex.style = dashLineStyle;
      }

      const currentEdge: StyledEdge = {point1: pointIndex + 1, point2: nextPointIndex + 1, command: new LineTo({x: 0, y: 0})};
      const isEdgeVisible: boolean =
          (baseVertexCount === 3 && linesEqual(sweepLine, {p0: currentPoint, p1: nextPoint})) ||
          (isCurrentPointVisible || isNextPointVisible);

      if (isEdgeVisible) {
        currentEdge.style = dashLineStyle;
      }

      edges.push(edgeFromTopVertex);
      edges.push(currentEdge);
    }

    return {
      points,
      edges
    };
  }
}
